import React, { useContext, useState } from "react";
import {
  Form,
  Input,
  Button,
  Select,
  Radio,
  Space,
  Popover,
  Checkbox,
} from "antd";
import { AppContext } from "../../context";
import { countryList } from "../../constants/countryList";
import { districtList } from "../../constants/districtList";
import { BsLock } from "react-icons/bs";
import { MailOutlined } from "@ant-design/icons";
import { FaInfoCircle } from "react-icons/fa";
import "./UserInfoForm.css";

const tailLayout = {
  wrapperCol: {
    offset: 4,
    span: 16,
  },
};

function UserInfoForm({
  setIsModalVisible,
  form,
  onFinish,
  showPrivacyAndTerms,
  actionButtonText,
  disableUserCategorySelection = false,
}) {
  const info = useContext(AppContext);
  const [isPortugal, setIsPortugal] = useState(false);
  const [userCategory, setUserCategory] = useState(null);
  const [fields, setFields] = useState({});
  const { Option } = Select;
  return (
    <div style={{ width: "90%", margin: "auto auto" }}>
      <Form
        form={form}
        onFinish={onFinish}
        layout={"vertical"}
        initialValues={{
          language: info.language,
        }}
        size="small"
        onFieldsChange={() => {
          const fields = form.getFieldsValue();
          setFields({ ...fields });
        }}
      >
        <Form.Item
          name="email"
          rules={[
            {
              required: true,
              type: "email",
              message: info.t("insertEmail"),
            },
          ]}
        >
          <Input prefix={<MailOutlined />} placeholder="Email"></Input>
        </Form.Item>
        <Form.Item
          name="password"
          rules={[
            {
              required: true,
              message: "",
            },
            {
              validator: (_, value) =>
                /[*@!#%&()^~{}"$*-.,:;|ªº´`?<>]+/.test(value) &&
                value.length >= 10
                  ? Promise.resolve()
                  : Promise.reject(new Error(info.t("passwordRequirements"))),
            },
          ]}
        >
          <Input.Password prefix={<BsLock />} placeholder="Password" />
        </Form.Item>
        <Form.Item
          name="name"
          label={info.t("nameFirstAndLast")}
          rules={[
            {
              required: true,
              message: info.t("nameRequired"),
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="user_category"
          label={info.t("userCategory")}
          rules={[
            {
              required: disableUserCategorySelection ? false : true,
              message: info.t("userCategoryRequired"),
            },
          ]}
        >
          <Radio.Group disabled={disableUserCategorySelection}>
            <Radio value="visitor">{info.t("visitor")}</Radio>
            <Radio value="resident">{info.t("resident")}</Radio>
            <Radio value="company">{info.t("company")}</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          name="language"
          label={info.t("language")}
          rules={[
            {
              required: true,
              message: info.t("languageRequired"),
            },
          ]}
        >
          <Select>
            <Option value="pt">Português</Option>
            <Option value="en">English</Option>
            <Option value="es">Español</Option>
            <Option value="fr">Français</Option>
            <Option value="de">Deutsch</Option>
          </Select>
        </Form.Item>

        {fields.user_category === "company" && (
          <>
            <Form.Item
              name="company"
              label={info.t("company")}
              rules={[
                {
                  required: true,
                  message: info.t("companyRequired"),
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="company_vat"
              label={info.t("companyVAT")}
              rules={[
                {
                  required: true,
                  message: info.t("companyVATRequired"),
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="business_area" label={info.t("businessArea")}>
              <Input />
            </Form.Item>
          </>
        )}

        {fields.user_category !== "resident" && (
          <Form.Item
            name="country"
            label={info.t("country")}
            rules={[
              {
                required: true,
                message: info.t("countryRequired"),
              },
            ]}
          >
            <Select
              showSearch
              options={countryList.map((c) => ({ value: c, label: c }))}
              onChange={(value) => {
                if (value === "Portugal") setIsPortugal(true);
                else setIsPortugal(false);
                form.setFieldsValue({ district: "" });
              }}
            />
          </Form.Item>
        )}
        {isPortugal && (
          <Form.Item
            name="district"
            label={info.t("district")}
            rules={[
              {
                required: true,
                message: info.t("districtRequired"),
              },
            ]}
          >
            <Select
              showSearch
              options={districtList.map((d) => ({ value: d, label: d }))}
            />
          </Form.Item>
        )}
        {fields.user_category === "company" ? (
          <Form.Item name="address" label={info.t("companyAddress")}>
            <Input />
          </Form.Item>
        ) : (
          <Form.Item name="address" label={info.t("address")}>
            <Input />
          </Form.Item>
        )}
        <Form.Item name="postal_code" label={info.t("postalCode")}>
          <Input />
        </Form.Item>
        <Form.Item name="phone" label={info.t("phone")}>
          <Input />
        </Form.Item>
        {fields.user_category === "visitor" && (
          <Form.Item
            name="trip_motivation"
            label={info.t("tripMotivation")}
            rules={[
              {
                required: true,
                message: info.t("requiredTripMotivation"),
              },
            ]}
          >
            <Radio.Group>
              <Space direction="vertical" align="start">
                <Radio value={"business"}>{info.t("business")}</Radio>
                <Radio value={"leisure"}>{info.t("leisure")}</Radio>
                <Radio value={"businessAndLeisure"}>
                  {info.t("businessAndLeisure")}
                </Radio>
                <Radio value={"visitFamily"}>{info.t("visitFamily")}</Radio>
                <Radio value={"activeAndNature"}>
                  {info.t("activeAndNature")}
                </Radio>
                <Radio value={"healthAndWellbeing"}>
                  {info.t("healthAndWellbeing")}
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
        )}

        {showPrivacyAndTerms && (
          <>
            <Form.Item
              name="privacyPolicy"
              label={info.t("privacyPolicy")}
              valuePropName="checked"
              rules={[
                { required: true, message: "" },
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error(info.t("privacyPolicyRequired"))
                        ),
                },
              ]}
              style={{ textAlign: "left" }}
            >
              <Checkbox>
                {info.t("privacyPolicyAccepted")}{" "}
                <Popover
                  content={
                    <div style={{ padding: 10 }}>
                      <a
                        href="https://aldeiashistoricasdeportugal.com/politica-de-privacidade/"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {info.t("privacyPolicy")}
                      </a>
                    </div>
                  }
                >
                  <FaInfoCircle />
                </Popover>
              </Checkbox>
            </Form.Item>
            <Form.Item
              name="termsAndConditions"
              label={info.t("termsAndConditions")}
              valuePropName="checked"
              rules={[
                { required: true, message: "" },
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error(info.t("termsAndConditionsRequired"))
                        ),
                },
              ]}
              style={{ textAlign: "left" }}
            >
              <Checkbox>
                {info.t("termsAndConditionsAccepted")}{" "}
                <Popover
                  content={
                    <div style={{ padding: 10 }}>
                      <a
                        href="https://aldeiashistoricasdeportugal.com/termos-e-condicoes/"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {info.t("termsAndConditions")}
                      </a>
                    </div>
                  }
                >
                  <FaInfoCircle />
                </Popover>
              </Checkbox>
            </Form.Item>
          </>
        )}
        {/* <Form.Item {...tailLayout}>
          <Button type="primary" htmlType="submit">
            {info.t("save")}
          </Button>
          <Button htmlType="button" onClick={() => setIsModalVisible(false)}>
            {info.t("cancel")}
          </Button>
        </Form.Item> */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
            gap: 10,
          }}
        >
          <Button
            type="primary"
            htmlType="submit"
            style={{
              // width: "100%",
              flexGrow: 1,
              marginLeft: -1,
              marginBottom: 10,
              backgroundColor: "#5382a4",
            }}
          >
            {actionButtonText}
          </Button>
          <Button
            type="default"
            htmlType="button"
            onClick={() => setIsModalVisible(false)}
            style={{ flexGrow: 1 }}
          >
            {info.t("cancel")}
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default UserInfoForm;
